/**
 * Typed calls against the advisor API (src/api/advisor/*).
 *
 * Every route here requires a bearer whose JWT carries a non-null
 * `advisor_firm_id` claim — the server's `requireAdvisor()` gate. A plain
 * advisor is scoped to their own book; a `firm_admin` sees the whole firm.
 * Scoping is enforced server-side in SQL; nothing here can widen it.
 */
import type { ApiClient } from './apiClient';

export interface DashboardAggregates {
  totalClients: number;
  sealedMessageCount: number;
  pendingAttestationsCount: number;
  upcomingEventsCount: number;
  recentlyDeliveredCount: number;
  eventsHorizonDays: number;
  recentlyDeliveredDays: number;
}

export interface ClientRosterRow {
  clientUserId: string;
  fullName: string;
  email: string;
  vaultId: string | null;
  vaultStatus: string | null;
  vaultCreatedAt: string | null;
  lastCheckinAt: string | null;
  nextCheckinDueAt: string | null;
  messageCount: number;
  sealedCount: number;
  recipientCount: number;
}

export interface AdvisorVaultMetadata {
  vaultId: string;
  vaultName: string;
  vaultStatus: string;
  messageCount: number;
  draftCount: number;
  sealedCount: number;
  deliveredCount: number;
  recipientCount: number;
  triggerTypeDistribution: Record<string, number>;
  lastCheckinAt: string | null;
  nextCheckinDueAt: string | null;
  checkinStatus: string | null;
  /** Always null. Message titles are the client's private content, never the advisor's. */
  title: null;
}

export interface PendingAttestationRow {
  userId: string;
  fullName: string;
  email: string;
  signupAt: string;
  vaultStatus: string | null;
  ageHours: number;
  isNewToday: boolean;
  isWaitingOver48h: boolean;
  emailVerified: boolean;
  phoneVerified: boolean;
  publicKeyRegistered: boolean;
}

export interface AttestationQueue {
  rows: PendingAttestationRow[];
  total: number;
  limit: number;
  offset: number;
}

export interface UpcomingEvent {
  triggerId: string;
  vaultId: string;
  clientName: string;
  triggerType: string;
  lifeEventType: string | null;
  eventDate: string;
  daysAway: number;
}

export interface AuditEvent {
  id: string;
  vaultId: string | null;
  messageId: string | null;
  actorRole: string | null;
  action: string;
  targetType: string | null;
  targetId: string | null;
  metadata: unknown;
  createdAt: string;
}

export interface AuditPage {
  rows: AuditEvent[];
  total: number;
  limit: number;
  offset: number;
}

export interface BulkInviteOutcome {
  row: number;
  email: string;
  ok: boolean;
  inviteId?: string;
  error?: string;
}

export interface BulkInviteResult {
  totalRows: number;
  successCount: number;
  failureCount: number;
  results: BulkInviteOutcome[];
}

export interface BulkAttestResult {
  attestedCount: number;
  results: Array<{ clientUserId: string; ok: boolean; reason?: string }>;
}

function qs(params: Record<string, string | number | undefined>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') p.set(k, String(v));
  }
  const s = p.toString();
  return s === '' ? '' : `?${s}`;
}

export function getDashboard(api: ApiClient): Promise<DashboardAggregates> {
  return api.get<DashboardAggregates>('/api/advisor/dashboard');
}

export function getClients(
  api: ApiClient,
  opts: { search?: string; sort?: 'alpha' | 'checkin_due' | 'status' } = {},
): Promise<{ rows: ClientRosterRow[] }> {
  return api.get<{ rows: ClientRosterRow[] }>(
    `/api/advisor/clients${qs({ search: opts.search, sort: opts.sort })}`,
  );
}

export function getVaultMetadata(api: ApiClient, vaultId: string): Promise<AdvisorVaultMetadata> {
  return api.get<AdvisorVaultMetadata>(`/api/advisor/vaults/${encodeURIComponent(vaultId)}/metadata`);
}

export function getAttestationQueue(
  api: ApiClient,
  opts: { limit?: number; offset?: number; vaultStatus?: string; since?: string } = {},
): Promise<AttestationQueue> {
  return api.get<AttestationQueue>(`/api/advisor/attestations/queue${qs({ ...opts })}`);
}

export function attestClient(api: ApiClient, userId: string): Promise<{ attestedAt: string }> {
  return api.post<{ attestedAt: string }>(
    `/api/advisor/attestations/${encodeURIComponent(userId)}/attest`,
    {},
  );
}

export function bulkAttest(api: ApiClient, clientUserIds: string[]): Promise<BulkAttestResult> {
  return api.post<BulkAttestResult>('/api/advisor/attestations/bulk-attest', { clientUserIds });
}

export function getUpcomingEvents(api: ApiClient, days = 30): Promise<{ events: UpcomingEvent[] }> {
  return api.get<{ events: UpcomingEvent[] }>(`/api/advisor/events/upcoming${qs({ days })}`);
}

export function getAuditLog(
  api: ApiClient,
  opts: { limit?: number; offset?: number; vaultId?: string; eventType?: string; since?: string; until?: string } = {},
): Promise<AuditPage> {
  return api.get<AuditPage>(`/api/advisor/audit-log${qs({ ...opts })}`);
}

export function createInvite(
  api: ApiClient,
  input: {
    clientEmail: string;
    clientDisplayName?: string;
    inviteMessage?: string;
    /**
     * The advisor affirms they personally know this invitee. The client is then
     * attested automatically on redemption — no second post-signup click.
     */
    advisorAttested?: boolean;
  },
): Promise<{ inviteId: string; inviteUrl: string; expiresAt: string }> {
  return api.post('/api/advisor/invites', input);
}

export function bulkInvite(
  api: ApiClient,
  csv: string,
  advisorAttested = false,
): Promise<BulkInviteResult> {
  return api.post<BulkInviteResult>('/api/advisor/invites/bulk', { csv, advisorAttested });
}

/**
 * Generate a senior recording link ("Dorothy flow"). Returns the one-time token
 * — surfaced ONCE, never stored unhashed. The client (vault owner) must APPROVE
 * it before the senior can record (M-3); the link is inert until then.
 */
export function createSeniorToken(
  api: ApiClient,
  input: { vaultId: string; expiresAt: string; preSetLabel?: string | null },
): Promise<{ tokenId: string; token: string; expiresAt: string; requiresOwnerApproval: true }> {
  return api.post('/api/advisor/senior-tokens', input);
}

/**
 * The CSV export route is bearer-gated like every other advisor route, so a
 * plain `window.location.href` navigation would 401 (it sends no Authorization
 * header). Fetch it with the bearer, then hand the browser a Blob.
 */
export async function downloadAuditCsv(
  accessToken: string,
  opts: { vaultId?: string; eventType?: string; since?: string; until?: string } = {},
): Promise<void> {
  const res = await fetch(`/api/advisor/audit-log/export.csv${qs({ ...opts })}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) throw new Error(`Export failed (${res.status})`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'advisor-audit-log.csv';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// --- Device recovery ---------------------------------------------------------
// An advisor is the fast door for a client who has lost their phone AND their
// Recovery Kit: verifying them face to face collapses the 72-hour wait. Until
// this existed the endpoint was reachable only by knowing a UUID nobody was
// ever shown, which meant in practice it was not reachable at all.

export interface PendingRecoveryRow {
  id: string;
  status: 'requested' | 'ready';
  requestedAt: string;
  coolingOffEndsAt: string;
  attestedAt: string | null;
  requestIp: string | null;
  requestUserAgent: string | null;
  ownerName: string;
  ownerEmail: string;
}

export function getPendingRecoveries(api: ApiClient): Promise<{ recoveries: PendingRecoveryRow[] }> {
  return api.get<{ recoveries: PendingRecoveryRow[] }>('/api/recovery/pending');
}

export function attestRecovery(
  api: ApiClient,
  recoveryId: string,
  method: 'video_call' | 'in_person',
  note: string,
): Promise<{ ready: true }> {
  return api.post<{ ready: true }>('/api/recovery/attest', { recoveryId, method, note });
}

// ---------------------------------------------------------------------------
// Firm succession — staff who leave, and books that move with them.
// ---------------------------------------------------------------------------

export interface FirmMemberRow {
  userId: string;
  fullName: string | null;
  email: string;
  isPrimary: boolean;
  clients: number;
  isYou: boolean;
}

export function getFirmMembers(api: ApiClient): Promise<{ members: FirmMemberRow[] }> {
  return api.get('/api/advisor/firm/members');
}

export interface TransferResult {
  transferId: string;
  clientsMoved: number;
  vaultsMoved: number;
  invitesMoved: number;
  departingRevoked: boolean;
}

export function transferBook(
  api: ApiClient,
  fromUserId: string,
  args: { toAdvisorId: string; reason?: string; revokeDeparting: boolean },
): Promise<TransferResult> {
  return api.post(`/api/advisor/firm/members/${fromUserId}/transfer-book`, {
    to_advisor_id: args.toAdvisorId,
    reason: args.reason ?? null,
    revoke_departing: args.revokeDeparting,
  });
}

export function revokeAdvisor(
  api: ApiClient,
  userId: string,
  reason?: string,
): Promise<{ userId: string; passkeysRevoked: number }> {
  return api.post(`/api/advisor/firm/members/${userId}/revoke`, { reason: reason ?? null });
}
